import React from "react";
import PropTypes from "prop-types";
import { Alert, Checkbox, Grid } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import { coresDoTema } from "assets/coresTema";

// Blocos do HTML: cada um é uma linha do texto, como o backend conta (MalaDiretaModeloDocxService: um parágrafo por linha)
const BLOCOS = new Set(["P", "DIV", "LI", "UL", "OL", "TABLE", "TBODY", "THEAD", "TFOOT", "TR", "TD", "TH", "H1", "H2", "H3",
  "H4", "H5", "H6", "BLOCKQUOTE", "PRE"]);

/**
 * O texto do editor como o backend conta as ocorrências: uma linha por parágrafo (e por quebra de linha), espaços
 * seguidos como um e sem espaço nas pontas da linha. Cada letra guarda o nó de texto e a posição de onde veio, para
 * selecionar ou trocar o trecho no próprio HTML (as imagens não contam).
 */
export const mapaDoTexto = (raiz) => {
  const letras = [];
  let linha = [];
  const fechar = () => {
    while (linha.length && linha[0].c === " ") linha.shift();
    while (linha.length && linha[linha.length - 1].c === " ") linha.pop();
    letras.push(...linha, { c: "\n", no: null, pos: -1 });
    linha = [];
  };
  const visitar = (no) => {
    if (no.nodeType === 3) {
      for (let i = 0; i < no.data.length; i += 1) {
        let c = no.data[i];
        if (/[\s\u00a0]/.test(c)) {
          if (linha.length && linha[linha.length - 1].c === " ") continue;
          c = " ";
        }
        linha.push({ c, no, pos: i });
      }
      return;
    }
    if (no.nodeType !== 1 || no.nodeName === "SCRIPT" || no.nodeName === "STYLE") return;
    if (no.nodeName === "BR") {
      fechar();
      return;
    }
    const bloco = BLOCOS.has(no.nodeName);
    if (bloco && linha.length) fechar();
    no.childNodes.forEach(visitar);
    if (bloco) fechar();
  };
  raiz.childNodes.forEach(visitar);
  if (linha.length) fechar();
  return { texto: letras.map((l) => l.c).join(""), letras };
};

/** Início e fim da n-ésima ocorrência do trecho no texto (contadas sem sobrepor, como o backend). */
export const acharTrecho = (texto, trecho, ocorrencia = 0) => {
  const t = (trecho || "").replace(/[\s\u00a0]+/g, " ").trim();
  if (!t) return null;
  let pos = texto.indexOf(t);
  for (let n = 0; pos >= 0; n += 1) {
    if (n === ocorrencia) return { inicio: pos, fim: pos + t.length };
    pos = texto.indexOf(t, pos + t.length);
  }
  return null;
};

/** Faixa do HTML que corresponde ao trecho (da 1ª à última letra dele). */
const faixa = (doc, mapa, achado) => {
  const primeira = mapa.letras[achado.inicio];
  const ultima = mapa.letras[achado.fim - 1];
  const r = doc.createRange();
  r.setStart(primeira.no, primeira.pos);
  r.setEnd(ultima.no, ultima.pos + 1);
  return r;
};

/**
 * Troca o trecho pela variável mantendo a formatação dele (o "R$ ____" em negrito e vermelho continua assim): o
 * trecho sai e a variável entra no nó de texto da 1ª letra, com o negrito, a cor e o tamanho de lá.
 */
export const trocarTrecho = (editor, mapa, achado, variavel) => {
  const primeira = mapa.letras[achado.inicio];
  faixa(editor.ed, mapa, achado).deleteContents();
  primeira.no.insertData(primeira.pos, variavel);
};

/**
 * Sugestões da importação do .docx (MalaDiretaModeloDocxService): os trechos do modelo que viram variável (dados de um
 * devedor digitados, campos em branco, a data do prazo, quem assina). Cada uma pode trocar de variável, ser mostrada no
 * texto, trocada ou ignorada; "Trocar as marcadas" faz todas de uma vez.
 */
function SugestoesDocx({ sugestoes, setSugestoes, avisos, variaveis, editorRef, darkMode }) {
  const cores = coresDoTema(darkMode);
  const editor = () => editorRef.current;
  const marcadas = sugestoes.filter((s) => s.marcada && s.variavel);

  const atualizar = (chave, mudancas) => setSugestoes((lista) => lista.map((s) => (s.chave === chave ? { ...s, ...mudancas } : s)));

  const mostrar = (s) => {
    const ed = editor();
    if (!ed) return;
    const mapa = mapaDoTexto(ed.editor);
    const achado = acharTrecho(mapa.texto, s.trecho, s.ocorrencia);
    if (!achado) {
      atualizar(s.chave, { naoEncontrada: true });
      return;
    }
    const r = faixa(ed.ed, mapa, achado);
    ed.s.selectRange(r);
    const el = mapa.letras[achado.inicio].no.parentElement;
    if (el && el.scrollIntoView) el.scrollIntoView({ block: "center" });
  };

  // Depois de trocar um trecho, as outras sugestões com o mesmo texto que vinham depois dele mudam de ocorrência
  const semAsTrocadas = (lista, trocadas) =>
    lista
      .filter((s) => !trocadas.some((t) => t.chave === s.chave))
      .map((s) => {
        const antes = trocadas.filter((t) => t.trecho === s.trecho && t.ocorrencia < s.ocorrencia).length;
        return antes ? { ...s, ocorrencia: s.ocorrencia - antes } : s;
      });

  const trocar = (lista) => {
    const ed = editor();
    if (!ed || !lista.length) return;
    const mapa = mapaDoTexto(ed.editor);
    const achadas = [];
    const perdidas = [];
    lista.forEach((s) => {
      const achado = acharTrecho(mapa.texto, s.trecho, s.ocorrencia);
      if (achado) achadas.push({ s, achado });
      else perdidas.push(s.chave);
    });
    // Do fim para o começo: trocar um trecho não muda o nó e a posição dos que vêm antes
    achadas.sort((a, b) => b.achado.inicio - a.achado.inicio).forEach(({ s, achado }) => trocarTrecho(ed, mapa, achado, s.variavel));
    ed.synchronizeValues();
    setSugestoes((atual) =>
      semAsTrocadas(atual, achadas.map((a) => a.s)).map((s) => (perdidas.includes(s.chave) ? { ...s, naoEncontrada: true, marcada: false } : s))
    );
  };

  if (!sugestoes.length && !avisos.length) return null;

  return (
    <MDBox mt={2}>
      {avisos.map((a) => (
        <Alert key={a} severity="warning" sx={{ mb: 1, fontSize: "0.85rem" }}>{a}</Alert>
      ))}
      {sugestoes.length > 0 && (
        <MDBox p={2} sx={{ border: `1px solid ${cores.borda}`, borderRadius: 2, backgroundColor: cores.painel }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1} mb={0.5}>
            <MDTypography variant="button" fontWeight="bold" color="text">
              Trechos do .docx que podem virar variável ({sugestoes.length})
            </MDTypography>
            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton
                size="small"
                variant="outlined"
                color="info"
                onClick={() => setSugestoes((lista) => lista.map((s) => ({ ...s, marcada: !!s.variavel && !s.naoEncontrada })))}
              >
                Marcar todas
              </MDButton>
              <MDButton size="small" variant="contained" color="info" disabled={!marcadas.length} onClick={() => trocar(marcadas)}>
                Trocar as marcadas ({marcadas.length})
              </MDButton>
            </MDBox>
          </MDBox>
          <MDTypography variant="caption" color="text" display="block" mb={1}>
            O sistema achou no modelo dados de um devedor digitados, campos em branco, o prazo e o nome de quem assina. Confira a
            variável de cada trecho e troque: no envio, cada ficha recebe os seus dados. &quot;Mostrar&quot; seleciona o trecho no
            texto abaixo.
          </MDTypography>
          {sugestoes.map((s, i) => (
            <Grid
              container
              spacing={1}
              alignItems="center"
              key={s.chave}
              sx={{ py: 0.75, borderTop: i ? `1px solid ${cores.divisoria}` : "none" }}
            >
              <Grid item xs="auto">
                <Checkbox
                  size="small"
                  checked={!!s.marcada && !!s.variavel}
                  disabled={!s.variavel || !!s.naoEncontrada}
                  onChange={(e) => atualizar(s.chave, { marcada: e.target.checked })}
                />
              </Grid>
              <Grid item xs={10} md={4}>
                <MDTypography variant="button" fontWeight="medium" color="text" sx={{ wordBreak: "break-word" }}>
                  &quot;{s.trecho}&quot;
                </MDTypography>
                <MDTypography variant="caption" color={s.naoEncontrada ? "error" : "text"} display="block">
                  {s.naoEncontrada ? "Não está mais no texto (foi editado ou já trocado)." : s.motivo}
                </MDTypography>
              </Grid>
              <Grid item xs={12} md={4}>
                <MDSelect
                  name={`sugestao-${s.chave}`}
                  options={variaveis}
                  getOptionLabel={(v) => v.nome}
                  getOptionValue={(v) => v.id}
                  value={variaveis.find((v) => v.id === s.variavel) || null}
                  onChange={(v) => atualizar(s.chave, { variavel: v ? v.id : null, marcada: !!v })}
                  placeholder="Escolha a variável..."
                  isClearable={false}
                />
              </Grid>
              <Grid item xs={12} md display="flex" gap={0.5} flexWrap="wrap" justifyContent={{ xs: "flex-start", md: "flex-end" }}>
                <MDButton size="small" variant="text" color="info" disabled={!!s.naoEncontrada} onClick={() => mostrar(s)}>
                  Mostrar
                </MDButton>
                <MDButton size="small" variant="outlined" color="info" disabled={!s.variavel || !!s.naoEncontrada} onClick={() => trocar([s])}>
                  Trocar
                </MDButton>
                <MDButton size="small" variant="text" color="secondary" onClick={() => setSugestoes((lista) => lista.filter((x) => x.chave !== s.chave))}>
                  Ignorar
                </MDButton>
              </Grid>
            </Grid>
          ))}
        </MDBox>
      )}
    </MDBox>
  );
}

SugestoesDocx.propTypes = {
  sugestoes: PropTypes.arrayOf(PropTypes.object).isRequired,
  setSugestoes: PropTypes.func.isRequired,
  avisos: PropTypes.arrayOf(PropTypes.string).isRequired,
  variaveis: PropTypes.arrayOf(PropTypes.object).isRequired,
  editorRef: PropTypes.shape({ current: PropTypes.any }).isRequired,
  darkMode: PropTypes.bool,
};

SugestoesDocx.defaultProps = {
  darkMode: false,
};

export default SugestoesDocx;
